/**
 * Versioned profile document for banner/modal view, dismiss, and CTA tracking.
 * Persisted via `save_profile('banner_views', doc)`.
 */

export const BANNER_VIEWS_PROFILE_KEY = 'banner_views';
export const BANNER_VIEWS_SCHEMA_VERSION = 1 as const;
export const BANNER_VIEWS_NOTE_MAX_LENGTH = 500;
export const BANNER_VIEWS_CTA_MAX = 50;

export type BannerId = string;
export type SurfaceId = string;

export type BannerScope = 'shared' | 'per-surface';
export type BannerDismissSource = 'user' | 'cta' | 'auto';

export type BannerCtaEvent = {
	id: string;
	at: number;
	surface?: string;
	href?: string;
};

export type BannerSurfaceState = {
	firstSeenAt?: number;
	lastSeenAt?: number;
	impressionCount?: number;

	dismissedAt?: number;
	lastDismissedAt?: number;
	dismissCount?: number;
	dismissSource?: BannerDismissSource;

	snoozeUntil?: number;

	ctas?: BannerCtaEvent[];
	lastCtaId?: string;
	lastCtaAt?: number;

	note?: string;
	extras?: Record<string, unknown>;

	/** Forward-compat: unknown keys from newer clients are preserved. */
	[key: string]: unknown;
};

export type BannerRecord = {
	schemaVersion: typeof BANNER_VIEWS_SCHEMA_VERSION;
	id: BannerId;
	scope: BannerScope;
	note?: string;

	firstSeenAt?: number;
	lastSeenAt?: number;
	impressionCount?: number;

	dismissedAt?: number;
	lastDismissedAt?: number;
	dismissCount?: number;
	dismissSource?: BannerDismissSource;

	/** Absolute ms; omit + dismissedAt = permanent hide. */
	snoozeUntil?: number;
	convertedAt?: number;

	ctas?: BannerCtaEvent[];
	lastCtaId?: string;
	lastCtaAt?: number;

	surfaces?: Record<SurfaceId, BannerSurfaceState>;
	extras?: Record<string, unknown>;

	/** Forward-compat: unknown keys from newer clients are preserved. */
	[key: string]: unknown;
};

export type BannerViewsDocument = {
	schemaVersion: typeof BANNER_VIEWS_SCHEMA_VERSION;
	banners: Record<BannerId, BannerRecord>;
};

export type BannerViewsWriteResult =
	| { ok: true; document: BannerViewsDocument }
	| { ok: false; errors: string[] };
